import {
  ETable,
  ETableBody,
  ETableCell,
  ETableHead,
  ETableHeader,
  ETableRow,
} from "@/shared/components/tables/e-table";
import { Report } from "@/services/backend/htz/report/types/report";
import t from "@/lang/lang";
import { AirHandlingUnitComponentName } from "@/services/backend/htz/ahu/components/air-handling-unit-data-designation";
import {
  Result,
  ResultDot,
} from "@/services/backend/htz/inspection/components/inspection-result";
import {
  joinFieldName,
  useHasFieldErrors,
} from "@/shared/lib/errors/err-validation-failed-context";
import { cn } from "@/shared/lib/utils";
import { useHtzReportShowQuery } from "@/services/backend/htz/report/api/report-api";
import { UUID } from "@bitory-gmbh/nidavellir";

export function ItemsTable({ report }: { report: Report }) {
  const { data } = useHtzReportShowQuery({ id: report.id });

  return (
    <ETable>
      <ETableHeader>
        <ETableRow>
          <ETableHead>{t("Komponente")}</ETableHead>
          <ETableHead>{t("Konstruktiv")}</ETableHead>
          <ETableHead>{t("Visuell")}</ETableHead>
          <ETableHead>{t("Mikrobiologisch")}</ETableHead>
          <ETableHead className="w-full">{t("Handlungsempfehlung")}</ETableHead>
          <ETableHead>{t("Ergebnis")}</ETableHead>
        </ETableRow>
      </ETableHeader>
      <ETableBody>
        {data &&
          data.items.map((item) => (
            <ItemRow key={item.id} report={report} itemId={item.id} />
          ))}
      </ETableBody>
    </ETable>
  );
}

function ItemRow({ report, itemId }: { report: Report; itemId: UUID }) {
  const { data } = useHtzReportShowQuery({ id: report.id });
  const item = data?.items.find((i) => i.id === itemId);
  // The fields of an item are derived from the assessments of its
  // component. The assessment tables reset their errors.
  const { hasErrors: recommendationsHasErrors } = useHasFieldErrors(
    joinFieldName("items", itemId, "recommendations"),
  );

  if (!data || !item) return;

  return (
    <ETableRow key={item.id}>
      <ETableCell className="align-top font-medium">
        <div className="py-2">
          <AirHandlingUnitComponentName
            id={report.ahuDataId!}
            componentId={item.componentId}
          />
        </div>
      </ETableCell>
      <ETableCell className="align-top">
        <div className="py-2">
          <ItemResultDot
            result={item.structuralInspectionResult}
            fieldName={joinFieldName(
              "items",
              itemId,
              "structuralInspectionResult",
            )}
          />
        </div>
      </ETableCell>
      <ETableCell className="align-top">
        <div className="py-2">
          <ItemResultDot
            result={item.visualInspectionResult}
            fieldName={joinFieldName("items", itemId, "visualInspectionResult")}
          />
        </div>
      </ETableCell>
      <ETableCell className="align-top">
        <div className="py-2">
          <ItemResultDot
            result={item.microbialInspectionResult}
            fieldName={joinFieldName(
              "items",
              itemId,
              "microbialInspectionResult",
            )}
          />
        </div>
      </ETableCell>
      <ETableCell
        className={cn(
          recommendationsHasErrors && "inset-ring inset-ring-red-500",
        )}
      >
        <div className="py-2">
          {item.recommendations.map((r) => (
            <div key={r}>{r}</div>
          ))}
        </div>
      </ETableCell>
      <ETableCell className="align-top">
        <div className="py-2">
          <ItemResultDot
            result={item.result}
            fieldName={joinFieldName("items", itemId, "result")}
          />
        </div>
      </ETableCell>
    </ETableRow>
  );
}

function ItemResultDot({
  result,
  fieldName,
}: {
  result: Result;
  fieldName: string;
}) {
  const { hasErrors } = useHasFieldErrors(fieldName);
  return <ResultDot result={result} hasError={hasErrors} />;
}
